import React from 'react';
import { motion } from 'framer-motion';

/**
 * PillarContentRenderer
 * A simple markdown renderer optimized for pillar page content.
 * Handles:
 * - **bold** text
 * - Lists with - prefix (rendered as styled bullets, NOT checkmarks)
 * - Subheadings formatted as **Text** on its own line → becomes h3
 * - Regular paragraphs
 * - Horizontal rules (---)
 */
export const PillarContentRenderer = ({ content }) => {
    if (!content) return null;

    // Split content by double newlines to create sections
    const sections = content.split('\n\n').filter(s => s.trim());

    return (
        <div className="space-y-4 text-slate-300 leading-relaxed text-sm">
            {sections.map((section, idx) => {
                const trimmed = section.trim();

                // Skip empty sections
                if (!trimmed) return null;

                // Horizontal rule
                if (trimmed === '---') {
                    return (
                        <hr key={idx} className="border-slate-700/50 my-6" />
                    );
                }

                // Subheading: **Text** on its own (bold standalone paragraph)
                if (/^\*\*[^*]+\*\*$/.test(trimmed)) {
                    const headingText = trimmed.replace(/^\*\*/, '').replace(/\*\*$/, '');
                    return (
                        <motion.h3
                            key={idx}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.02 }}
                            className="text-lg font-bold text-white mt-6 mb-3"
                        >
                            {headingText}
                        </motion.h3>
                    );
                }

                // Bullet list (lines starting with -)
                if (trimmed.includes('\n-') || trimmed.startsWith('- ')) {
                    const lines = trimmed.split('\n');
                    const items = lines.filter(line => line.trim().startsWith('- '));
                    const preamble = lines.filter(line => !line.trim().startsWith('- ')).join('\n').trim();

                    return (
                        <div key={idx}>
                            {preamble && (
                                <motion.p
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: idx * 0.02 }}
                                    className="mb-2"
                                >
                                    {renderBold(preamble)}
                                </motion.p>
                            )}
                            <ul className="space-y-2 ml-4">
                                {items.map((item, itemIdx) => {
                                    const text = item.replace(/^-\s*/, '').trim();
                                    return (
                                        <motion.li
                                            key={itemIdx}
                                            initial={{ opacity: 0, x: -5 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: (idx + itemIdx * 0.1) * 0.02 }}
                                            className="flex gap-2 items-start"
                                        >
                                            <span className="text-primary mt-1">•</span>
                                            <span>{renderBold(text)}</span>
                                        </motion.li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                }

                // Regular paragraph - render with bold support
                return (
                    <motion.p
                        key={idx}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.02 }}
                    >
                        {renderBold(trimmed)}
                    </motion.p>
                );
            })}
        </div>
    );
};

/**
 * Helper function to render **bold** text within a string
 */
const renderBold = (text) => {
    if (!text) return null;

    // Split by **bold** pattern
    const parts = text.split(/\*\*(.*?)\*\*/g);

    if (parts.length === 1) {
        // No bold markers, return as-is (handle newlines)
        return text.split('\n').map((line, i) => (
            <React.Fragment key={i}>
                {i > 0 && <br />}
                {line}
            </React.Fragment>
        ));
    }

    return parts.map((part, idx) => {
        if (idx % 2 === 1) {
            // Odd indices are the bold content
            return (
                <span key={idx} className="font-semibold text-white">
                    {part}
                </span>
            );
        }
        // Even indices are regular text (handle newlines)
        return part.split('\n').map((line, i) => (
            <React.Fragment key={`${idx}-${i}`}>
                {i > 0 && <br />}
                {line}
            </React.Fragment>
        ));
    });
};

export default PillarContentRenderer;
